<template>
  <fragment>
    <mc-app-header title="Edit Challenge">
      <template #left>
        <div class="level-item">
          <b-taglist v-if="!!challenges.selected.version" attached>
            <b-tag type="is-dark">version</b-tag>
            <b-tag type="is-info">
              {{ challenges.selected.version }}
            </b-tag>
          </b-taglist>
        </div>
      </template>
      <template #right>
        <fragment v-show="challenges.selected.id === $route.params.id">
          <div class="level-item">
            <b-button
              :loading="challenges.submitting"
              type="is-primary"
              @click="updateChallengeHandler"
              >Update</b-button
            >
          </div>
          <div class="level-item">
            <b-dropdown
              :disabled="challenges.publishing"
              position="is-bottom-left"
            >
              <button slot="trigger" class="button is-primary">
                <span>Publish</span>
                <b-icon icon="menu-down" />
              </button>

              <b-dropdown-item @click="publishChallengeHandler('admin')">
                <div class="media">
                  <div class="media-content">
                    <div class="title is-6">For Admin</div>
                    <div class="content is-small">
                      Challenge will only be published for admin users.
                    </div>
                  </div>
                </div>
              </b-dropdown-item>
              <b-dropdown-item @click="publishChallengeHandler('user')">
                <div class="media">
                  <div class="media-content">
                    <div class="title is-6">For Users</div>
                    <div class="content is-small">
                      Challenge will be published for all users and freezed i.e,
                      questions, puzzle, criteria etc. can't be changed.
                    </div>
                  </div>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </fragment>
      </template>
    </mc-app-header>
    <mc-app-body :submitting="challenges.submitting || challenges.publishing">
      <div class="edit-challenge section">
        <b-loading
          v-if="challenges.loadingSelected"
          active
          :is-full-page="false"
        />
        <mc-error-layout
          v-else-if="challenges.selectError"
          message="Failed to load challenge!"
          :retry-fn="() => selectChallenge($route.params.id)"
        />
        <div v-else>
          <div class="edit-challenge__form mc-container">
            <b-field label="Name" expanded>
              <b-input
                v-model="challenges.selected.name"
                icon="format-title"
                placeholder="Name"
                type="text"
              />
            </b-field>
            <b-field class="no-group" label="Poster">
              <mc-image-input
                v-model="challenges.selected.poster"
                icon="image"
                placeholder="Poster Link"
                type="POSTER"
              />
            </b-field>
            <b-field class="no-group" label="Related Concepts">
              <mc-concept-select
                v-model="challenges.selected.details.relatedConcepts"
                multiple
                @input="updateRelatedTopicsHandler"
              />
            </b-field>
            <b-field class="no-group" label="Related Topics">
              <mc-topic-select
                v-model="challenges.selected.details.relatedTopics"
                multiple
              />
            </b-field>
            <b-field class="no-group" label="Description">
              <mc-markdown-editor
                v-model="challenges.selected.details.description"
              />
            </b-field>
            <b-field class="no-group" label="Puzzle">
              <mc-markdown-editor
                v-model="challenges.selected.details.puzzle"
              />
            </b-field>
            <questions-list />
            <b-field class="is-divider">
              <b-switch v-model="challenges.selected.details.hasSubjective">
                Has Subjective?
              </b-switch>
            </b-field>
            <fragment v-if="challenges.selected.details.hasSubjective">
              <b-field class="no-group" label="Model Solution">
                <b-field>
                  <b-input
                    v-model="challenges.selected.details.solution"
                    icon="file-pdf"
                    placeholder="Model Solution"
                    type="text"
                    expanded
                    @input="$emit('input', $event)"
                  />
                  <p class="control">
                    <b-button @click="uploadSolutionHandler"
                      >Upload Solution</b-button
                    >
                  </p>
                </b-field>
              </b-field>
              <criteria-editor v-model="challenges.selected.details.criteria" />
              <b-field class="no-group" label="Rereview Descriptor">
                <b-input
                  v-model="challenges.selected.details.rereviewDescriptor"
                  icon="help-circle"
                  placeholder="Rereview Descriptor"
                  type="text"
                />
              </b-field>
            </fragment>
          </div>
        </div>
      </div>
    </mc-app-body>
    <question-editor />
  </fragment>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { DateTime as LuxonDateTime } from 'luxon';

import CriteriaEditor from './CriteriaEditor';
import QuestionEditor from '../../ui/QuestionEditor';
import QuestionsList from './QuestionsList';
import { openMcFileUploadModal } from '../../ui/general';

const publishDescriptionMap = {
  admin: 'admins',
  user: 'all users'
};

export default {
  components: {
    CriteriaEditor,
    QuestionEditor,
    QuestionsList
  },
  data() {
    return {
      stateOptions: ['open', 'closed']
    };
  },
  computed: {
    ...mapState(['challenges'])
  },
  created() {
    this.loadChallenge();
  },
  methods: {
    ...mapActions('challenges', [
      'selectChallenge',
      'publishChallenge',
      'updateChallenge',
      'updateRelatedTopics',
      'updateSolution'
    ]),
    loadChallenge() {
      this.selectChallenge(this.$route.params.id);
    },
    parsePublishError(error) {
      switch (error.message) {
        case 'publish_not_allowed':
          return 'This challenge is already published for all users!';
        case 'invalid_hint_id':
          return 'Invalid question marked as hint!';
        case 'invalid_challenge':
          return 'Challenge is not configured correctly!';
        default:
          return 'Failed to publish challenge';
      }
    },
    publishChallengeHandler(publishLevel) {
      const msg = publishDescriptionMap[publishLevel];
      this.$buefy.dialog.confirm({
        message: `
        Proceeding will save all your unsaved changes and publish a this
        challenge for ${msg}.`,
        confirmText: 'Publish Challenge',
        onConfirm: async () => {
          try {
            await this.updateChallenge({ ...this.challenges.selected });
            await this.publishChallenge({ publishLevel });
            this.$buefy.toast.open(`Challenge has been published for ${msg}`);
            this.loadChallenge();
          } catch (error) {
            this.$buefy.toast.open({
              message: this.parsePublishError(error),
              type: 'is-danger'
            });
          }
        }
      });
    },
    async updateChallengeHandler() {
      try {
        await this.updateChallenge({ ...this.challenges.selected });
        // If the user accidentally removes related topics that are then
        // auto-added back upon updating the challenge (because some related
        // concepts belong to them), then the UI doesn't immediately reflect
        // this. Hence this call is needed.
        this.updateRelatedTopics();

        this.$buefy.toast.open({
          message: 'Challenge updated successfully!',
          type: 'is-success'
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to update challenge!',
          type: 'is-danger'
        });
      }
    },
    async uploadSolutionHandler() {
      // await openSolutionUploadModal(this);
      // this.challenges.selected.details.solution = this.challenges.solutionUploadUrl;
      try {
        const uploadedSolutionUrl = await openMcFileUploadModal(this, {
          resourceState: 'challenges',
          fieldName: 'solution',
          title: 'Upload Solution (PDF)',
          pathPrefix: _ => 'challengeSolutions'
        });
        this.updateSolution(uploadedSolutionUrl);
      } catch (e) {
        this.$buefy.toast.open({
          message: `Updating Solution URL ${
            e === 'escape' || e === 'x' || e == 'outside'
              ? 'canceled'
              : 'failed'
          }!`,
          type: 'is-danger'
        });
      }
    },
    async updateRelatedTopicsHandler() {
      await this.updateRelatedTopics();
    }
  }
};
</script>

<style lang="scss" scoped>
.media-content {
  white-space: normal;
}

.no-group {
  margin-bottom: 1.5rem;
}

.edit-challenge {
  /deep/ .field.is-expanded {
    flex-basis: 0;
  }

  .card {
    &:not(:last-child) {
      margin-bottom: 1rem;
    }
  }

  .is-divider {
    display: flex;
    justify-content: center;
    position: relative;
    &:after,
    &:before {
      background: $light-color;
      content: '';
      padding: 0.1rem 0;
      -webkit-transform: translateY(0.7rem);
      -ms-transform: translateY(0.7rem);
      transform: translateY(0.7rem);
      width: 35%;
      position: absolute;
    }
    &:after {
      right: 0;
    }
    &:before {
      left: 0;
    }
  }
}
</style>
